import { Accordion, Anchor, Card, Grid, Text, Title, List, Paper, Space, createStyles } from "@mantine/core";
import { useState, useEffect } from "react";
import { useMutation } from "@apollo/client";
import { CircleNumber1, CircleNumber2, CircleNumber3, CircleNumber4, CircleNumber5 } from 'tabler-icons-react';
import SMA from "../../sample-trading-algo/SMA.js";
import Mean_reversion from "../../sample-trading-algo/Mean-reversion.js";
import Momentum from "../../sample-trading-algo/Momentum.js";
import exampleTradingBot from "../../sample-trading-algo/ExampleTradingBot.js";
import Step1JoinContest from "../../videos/join-contest.mp4";
import Step2aTradeManual from "../../videos/trade-manual.mp4";
import Step2bTradeCodeEditor from "../../videos/trade-code-editor.mp4";
import Step3ContestRanking from "../../videos/monitor-contest-ranking.mp4";
import Step4LeaveContest from "../../videos/leave-contest.mp4";
import "./TutorialPage.css"
import SyntaxHighlighter from 'react-syntax-highlighter';
import { ADD_LOG } from "../../queries/user-analytics"; // added fro tracking data
import { API_DOC_URL, EXAMPLE_CONNECTOR_URL } from "../../config/endpoints";

const useStyles = createStyles((theme) => ({
    root: {
        backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],
        borderRadius: theme.radius.sm,
    },

    item: {
        backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],
        border: '1px solid transparent',
        position: 'relative',
        zIndex: 0,
        transition: 'transform 150ms ease',

        '&[data-active]': {
            transform: 'scale(1.03)',
            backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.white,
            boxShadow: theme.shadows.md,
            borderColor: theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[2],
            borderRadius: theme.radius.md,
            zIndex: 1,
        },
    },

    chevron: {
        '&[data-rotate]': {
            transform: 'rotate(-180deg)',
        },
    },
    
    separator: {
        borderTop: `2px dashed ${theme.colorScheme === 'dark' ? theme.colors.dark[3] : theme.colors.gray[4]}`,
        borderRadius: theme.radius.xl,
        backgroundColor: 'inherit',
    },

    separatorActive: {
        borderWidth: 0,
        backgroundImage: theme.fn.linearGradient(45, theme.colors.blue[6], theme.colors.cyan[6]),
    },
}));

export default function TutorialPage() {
    const { classes } = useStyles();
    const [value, setValue] = useState("step1");
    const userId = 10000; 
    // added for tracking user data
    const [logEventMutation] = useMutation(ADD_LOG, {
        onCompleted: ({ addLog }) => {
            // Access the HTTP response directly
            const { status, message } = addLog;
            if (status === "success") {
                console.log("Event logged successfully:", message);
            } else {
                console.error("Failed to log event:", message);
            }
        },
        onError: (error) => {
            console.error("Error logging event:", error.message);
        }
    });

    const logEvent = (eventType, additionalData, _userid) => {
        const eventData = {
            userid: Number(_userid),
            event_type: eventType,
            timestamp: new Date().toISOString(),
            additional_data: JSON.stringify(additionalData)

        };

        logEventMutation({ variables: eventData });
    };
    useEffect(() => {
        logEvent('TutorialsPage', {}, userId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="container pad-top pad-btm">
            <Title order={2} mb="xl">NUSwap Tutorial</Title>
            <Grid grow gutter="40px" style={{ paddingBottom: "40px" }}>
                <Grid.Col>
                    <div className="slide-container">
                        <iframe
                            className="iframe-element"
                            src="https://docs.google.com/presentation/d/e/2PACX-1vTFIL98AQhjv9Y2qc7DJWsAnthuaNtGeOtoOeAbD3KcaBhBZFUwdZxrxMmSMiLz5Q/embed?start=false&loop=false&delayms=3000" 
                            frameborder="0" 
                            width="560" 
                            height="315"
                            allowfullscreen="true" 
                            mozallowfullscreen="true" 
                            webkitallowfullscreen="true"
                            title="NUSwap Tutorial Presentation">
                        </iframe>
                    </div>
                </Grid.Col>
                <Grid.Col className="iframe-container">
                    <Title p="lg">Sample Trading Algorithm</Title>
                    <Accordion value={value} onChange={setValue} variant="contained" sx={{ margin: 'auto', backgroundColor: "inherit", minWidth: '350px' }} classNames={classes} className={classes.root}>
                        <Accordion.Item value="sma">
                            <Accordion.Control>
                                <b>Simple Mean Average (SMA)</b>
                            </Accordion.Control>
                            <Accordion.Panel>
                                <SyntaxHighlighter language="python">
                                    {SMA}
                                </SyntaxHighlighter>
                            </Accordion.Panel>
                        </Accordion.Item>

                        <Accordion.Item value="mean-reversion">
                            <Accordion.Control>
                                <b>Mean Reversion</b>
                            </Accordion.Control>
                            <Accordion.Panel>
                                <SyntaxHighlighter language="python">
                                    {Mean_reversion}
                                </SyntaxHighlighter>
                            </Accordion.Panel>
                        </Accordion.Item>

                        <Accordion.Item value="momentum">
                            <Accordion.Control>
                                <b>Momentum</b>
                            </Accordion.Control>
                            <Accordion.Panel>
                                <SyntaxHighlighter language="python">
                                    {Momentum}
                                </SyntaxHighlighter>
                            </Accordion.Panel>
                        </Accordion.Item>
                    </Accordion>
                    <Card withBorder my="lg">
                        <Text>
                            Checkout the
                            <Anchor href={API_DOC_URL} target="_blank" style={{ fontWeight: 'bold'}}> List of APIs </Anchor>
                            to use in the inbuilt code editor
                        </Text>
                        <Text size="xs" color="dimmed" mt="xs">
                            Excel sheet (.xlsx) listing every NUSwap API endpoint with parameters and example responses.
                        </Text>
                    </Card>
                </Grid.Col>
                <Grid.Col className="iframe-container">
                    <Title p="lg">Code Your Own Trading Bot</Title>
                    <Text pl="xl">1. Copy NUSwapConnector Class and other functions you need from the Example Connector</Text>
                    <Text pb="xl" pl="xl">2. Use the functions to write your trading bot</Text>
                    <Accordion value={value} onChange={setValue} variant="contained" sx={{ margin: 'auto', backgroundColor: "inherit", minWidth: '350px' }} classNames={classes} className={classes.root}>
                        <Accordion.Item value="sma">
                            <Accordion.Control>
                                <b>Example Code</b>
                            </Accordion.Control>
                            <Accordion.Panel>
                                <SyntaxHighlighter language="python">
                                    {exampleTradingBot}
                                </SyntaxHighlighter>
                            </Accordion.Panel>
                        </Accordion.Item>
                    </Accordion>
                    <Card withBorder my="lg">
                        <Text>
                            Checkout the
                            <Anchor href={EXAMPLE_CONNECTOR_URL} target="_blank" style={{ fontWeight: 'bold'}}> Example Connector </Anchor>
                            to build your own trading bot
                        </Text>
                        <Text size="xs" color="dimmed" mt="xs">
                            Python file (API.py) containing the NUSwapConnector class. Save to your project and import to access NUSwap from your bot.
                        </Text>
                    </Card>
                </Grid.Col>
            </Grid>

            <List listStyleType="none">
                <Title p="lg">Instructions</Title>
                <List.Item p="lg" pb="md" icon={<CircleNumber1 fill="var(--color-nuswap-secondary-2)" color="white" size={30} />}>
                    <p>Join Contest:  Click on the 'Join' button to participate in ongoing competitions and showcase your skills.</p>
                </List.Item>  
                <Paper radius="md" p="xl" className="iframe-container" style={{backgroundColor: "rgba(231, 245, 255, 1)"}}>
                    <div className="iframe-container">
                        <video src={Step1JoinContest} width="100%" controls preload="metadata" style={{ maxWidth: "100%", height: "auto" }}>
                            Your browser does not support the video tag.
                        </video>
                    </div>
                </Paper>   
                <Space p="md"></Space>
                <List.Item p="lg" pb="md" icon={<CircleNumber2 fill="var(--color-nuswap-secondary-2)" color="white" size={30} />}>
                    <p>Trade (Manual): Start trading by manually inputting buy and sell orders through the trading interface</p>
                </List.Item> 
                <Paper radius="md" p="xl" className="iframe-container" style={{backgroundColor: "rgba(231, 245, 255, 1)"}}>
                    <div className="iframe-container">
                        <video src={Step2aTradeManual} width="100%" controls preload="metadata" style={{ maxWidth: "100%", height: "auto" }}>
                            Your browser does not support the video tag.
                        </video>
                    </div>
                </Paper>
                <Space p="md"></Space>
                <List.Item p="lg" pb="md" icon={<CircleNumber3 fill="var(--color-nuswap-secondary-2)" color="white" size={30} />}>
                    <p>Trade (Code Editor): Opt for an advanced approach by writing and executing trading algorithms directly within the code editor for automated transactions.
                        Sample trading algorithms can be found above to help you write your first algorithm. Note: This feature is only available on desktop view.</p>
                </List.Item>
                <Paper radius="md" p="xl" className="iframe-container" style={{backgroundColor: "rgba(231, 245, 255, 1)"}}>
                    <div className="iframe-container">
                        <video src={Step2bTradeCodeEditor} width="100%" controls preload="metadata" style={{ maxWidth: "100%", height: "auto" }}>
                            Your browser does not support the video tag.
                        </video>
                    </div> 
                </Paper>   
                <Space p="md"></Space>
                <List.Item p="lg" pb="md" icon={<CircleNumber4 fill="var(--color-nuswap-secondary-2)" color="white" size={30} />}>
                    <p>Monitor Contest Ranking: Check your performance by regularly using the P/L graph in the contest rankings to see where you stand compared to other participants</p>
                </List.Item> 
                <Paper radius="md" p="xl" className="iframe-container" style={{backgroundColor: "rgba(231, 245, 255, 1)"}}>
                    <div className="iframe-container">
                        <video src={Step3ContestRanking} width="100%" controls preload="metadata" style={{ maxWidth: "100%", height: "auto" }}>
                            Your browser does not support the video tag.
                        </video>
                    </div> 
                </Paper> 
                <Space p="md"></Space>
                <List.Item p="lg" pb="md" icon={<CircleNumber5 fill="var(--color-nuswap-secondary-2)" color="white" size={30} />}>
                    <p>Leave Contest: If you wish to withdraw from a contest, select the 'Leave Contest' option to exit gracefully and discontinue participation. 
                        Note: All progress are lost for the contest will be lost once you leave.</p>
                </List.Item> 
                <Paper radius="md" p="xl" className="iframe-container" style={{backgroundColor: "rgba(231, 245, 255, 1)"}}>
                    <div className="iframe-container">
                        <video src={Step4LeaveContest} width="100%" controls preload="metadata" style={{ maxWidth: "100%", height: "auto" }}>
                            Your browser does not support the video tag.
                        </video>
                    </div>  
                </Paper> 
            </List>
        </div >
    )
}
